import liffSdk from '@line/liff';
import apiService from '../services/api';
import { UserRegistrationData } from '../components/UserRegistrationPopup';
import { isLiffMockEnabled } from '../mocks/liff.mock';

// Get LIFF instance:
// - mock mode: window.liff (set in index.tsx)
// - real mode: use @line/liff SDK directly
const getLiff = (): any => {
  if (isLiffMockEnabled()) {
    const liffInstance = (window as any).liff;
    if (!liffInstance) {
      throw new Error('[LIFF Service] Mock LIFF is not initialized. Make sure LIFF mock is set up in index.tsx');
    }
    return liffInstance;
  }

  // Real LIFF SDK (from npm package)
  return liffSdk;
};

// Shorter alias for readability
const liff = new Proxy({}, {
  get: (target, prop) => {
    return getLiff()[prop];
  }
}) as any;

interface LiffProfile {
  userId: string;
  displayName: string;
  pictureUrl?: string;
  statusMessage?: string;
  email?: string;
}


export interface UserRegistrationState {
  isExistingUser?: boolean;
  needsRegistration: boolean;
  needsStoreRegistration?: boolean;
  storeName?: string;
}

// AuthContext連携用のコールバック型定義
interface AuthCallbacks {
  onUserAuthenticated?: (userId: string) => void;
  onRegistrationRequired?: (state: UserRegistrationState) => void;
  onStoreRegistrationRequired?: (storeName: string, userId: string) => void;
  // 退会済みアカウントでの再入場（080）。店舗登録ダイアログの代わりに再開ダイアログを出す
  onReactivationRequired?: (storeName: string, userId: string) => void;
}

const LIFF_INIT_RELOAD_GUARD_KEY = 'liff-init-reload-attempted';
const LIFF_STORE_KEY_PREFIX = 'LIFF_STORE';
const LAST_LIFF_ID_KEY = 'liff-last-liff-id';

// LINEアプリ内ブラウザが別店舗のLIFFネイティブコンテキストを使い回した際に
// liff.init()が「Invalid LIFF ID」で失敗するケースを検出する
function isInvalidLiffIdError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error);
  return /invalid liff id/i.test(message);
}

// 全店舗が同一オリジンで動くため、@line/liff SDKがlocalStorageに保存する
// liffIdごとの認証キャッシュ(LIFF_STORE:*)が店舗をまたいで残り続ける。
// 前回と異なるliffIdで初期化する際、古いliffIdのキャッシュが残っていると
// liff.init()が「Invalid LIFF ID」で失敗することがあるため、切り替え時に削除する
function clearStaleLiffStoreCache(currentLiffId: string): void {
  const lastLiffId = localStorage.getItem(LAST_LIFF_ID_KEY);
  if (!lastLiffId || lastLiffId === currentLiffId) return;

  const staleKeys: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.startsWith(LIFF_STORE_KEY_PREFIX) && !key.includes(currentLiffId)) {
      staleKeys.push(key);
    }
  }
  staleKeys.forEach((key) => localStorage.removeItem(key));
}

class LiffService {
  private initialized: boolean = false;
  private storeId: string = '';
  private liffId: string = '';
  private qrToken: string = ''; // QRコード経由のトークン
  private hasApplications: boolean = false; // 応募があるかどうか
  private registrationState: UserRegistrationState | null = null; // ユーザー登録の状態管理
  private authCallbacks: AuthCallbacks = {}; // AuthContext連携用
  private pendingUserId: string | null = null; // 店舗登録待ちのuserId
  private pendingLineUserId: string | null = null; // 店舗登録待ちのlineUserId

  // AuthContext用のコールバック設定
  setAuthCallbacks(callbacks: AuthCallbacks): void {
    this.authCallbacks = callbacks;
  }

  async initialize(): Promise<boolean> {
    if (this.initialized) {
      console.log('[LIFF] initialize - already initialized');
      return true;
    }

    console.log('[LIFF] initialize - starting');

    // URLパラメータからstoreIdを取得してliffIdをAPIから取得
    const urlResult = await this.initializeFromUrl();
    if (!urlResult.success) {
      console.error('[LIFF] initialize - initializeFromUrl failed:', urlResult.error);
      throw new Error(`店舗情報の取得に失敗しました: ${urlResult.error}`);
    }

    console.log('[LIFF] initialize - liffId=%s, storeId=%s, qrToken=%s', this.liffId, this.storeId, this.qrToken ? '(exists)' : '(empty)');

    try {
      clearStaleLiffStoreCache(this.liffId);

      // LIFF初期化のみを実行
      await liff.init({
        liffId: this.liffId,
        withLoginOnExternalBrowser: process.env.NODE_ENV !== "development", // localならfalse、それ以外はtrue
      })
      .then(async () => {
        console.log('[LIFF] initialize - liff.init() done, isLoggedIn=%s, isInClient=%s', liff.isLoggedIn(), liff.isInClient());
        // LIFF初期化完了後の処理
        // ログインしている場合のみユーザーチェックを実行
        if (liff.isLoggedIn()) {
          await this.checkUserAuthentication();
        } else {
          console.log('[LIFF] initialize - not logged in, handleLoginRequired');
          this.handleLoginRequired();
        }
      });

      console.log('[LIFF] initialize - complete: qrToken=%s, hasApplications=%s', this.qrToken ? '(exists)' : '(empty)', this.hasApplications);
      this.initialized = true;
      localStorage.setItem(LAST_LIFF_ID_KEY, this.liffId);
      sessionStorage.removeItem(LIFF_INIT_RELOAD_GUARD_KEY);
      return true;
    } catch (error) {
      console.error('[LIFF] initialize - error:', error);

      // 別店舗のLIFFを開いた直後に本店舗のリンクを踏むと、LINEアプリ内ブラウザが
      // ネイティブ側のLIFFコンテキストを使い回して「Invalid LIFF ID」になることがある。
      // 一度だけ強制リロードしてネイティブコンテキストを再同期させる。
      if (isInvalidLiffIdError(error) && !sessionStorage.getItem(LIFF_INIT_RELOAD_GUARD_KEY)) {
        console.warn('[LIFF] initialize - Invalid LIFF ID detected, forcing reload to resync native context');
        sessionStorage.setItem(LIFF_INIT_RELOAD_GUARD_KEY, '1');
        window.location.reload();
        // 通常はリロードでページごと離脱するが、環境によってはリロードが
        // 効かず離脱しないことがあるため、一定時間で元のエラーを投げて
        // 無限ローディングを防ぐ
        return new Promise<boolean>((_, reject) => {
          setTimeout(() => reject(error), 3000);
        });
      }

      throw error;
    }
  }

  /**
   * URLパラメータから店舗IDを取得してLIFF IDをセット
   */
  /**
   * liff.stateはパス+クエリ形式（例: "/jobs/123?shift=456"）の場合があるため、
   * クエリ部分のみを抽出してURLSearchParamsで解析する
   */
  private parseLiffState(liffState: string): URLSearchParams {
    const queryIndex = liffState.indexOf('?');
    if (queryIndex >= 0) {
      return new URLSearchParams(liffState.substring(queryIndex + 1));
    }
    // クエリ文字列形式（"store-id=xxx&qr-token=yyy"）の場合はそのまま解析
    return new URLSearchParams(liffState);
  }

  async initializeFromUrl(): Promise<{ success: boolean; error?: string }> {
    try {
      console.log('[LIFF] initializeFromUrl - URL:', window.location.href);
      console.log('[LIFF] initializeFromUrl - search:', window.location.search);
      console.log('[LIFF] initializeFromUrl - hash:', window.location.hash);

      const urlParams = new URLSearchParams(window.location.search);
      let storeId = urlParams.get('store-id') || this.storeId;
      let qrToken = urlParams.get('qr-token') || '';
      let shiftId = urlParams.get('shift') || '';
      let liffIdParam = urlParams.get('liff-id') || '';

      // ストレージからフォールバック（ページ更新・ホーム画面起動時にURLパラメータが失われるため）
      // qr-token, shift: sessionStorageに一時保存（セッション限り）
      // ※ store-id のストレージフォールバックは、現在のリンク由来の店舗シグナル
      //   （liff.state / hash / liff-id 逆引き / shift 逆引き）を全て試した後に回す（課題83）。
      //   ここで先にキャッシュを埋めると、別店舗のリンクを開いても直近店舗の store-id が
      //   優先され、その店舗の LIFF ID で liff.init して "Invalid LIFF ID" になる
      //   （店舗Aを直すと店舗Bが壊れる、という対称的なループの原因）。
      if (!qrToken) {
        qrToken = sessionStorage.getItem('liff-qr-token') || '';
      }
      if (!shiftId) {
        shiftId = sessionStorage.getItem('liff-shift-id') || '';
      }

      console.log('[LIFF] initializeFromUrl - direct params: store-id=%s, shift=%s, qr-token=%s', storeId, shiftId, qrToken ? '(exists)' : '(empty)');

      // LIFFリダイレクト後はクエリパラメータがliff.stateに格納されるため、そこからも取得を試みる
      if (!storeId || !shiftId || !qrToken || !liffIdParam) {
        const liffState = urlParams.get('liff.state');
        console.log('[LIFF] initializeFromUrl - liff.state:', liffState);
        if (liffState) {
          const liffStateParams = this.parseLiffState(liffState);
          if (!storeId) {
            storeId = liffStateParams.get('store-id') || '';
          }
          if (!shiftId) {
            shiftId = liffStateParams.get('shift') || '';
          }
          if (!qrToken) {
            qrToken = liffStateParams.get('qr-token') || '';
          }
          if (!liffIdParam) {
            liffIdParam = liffStateParams.get('liff-id') || '';
          }
          console.log('[LIFF] initializeFromUrl - from liff.state: store-id=%s, shift=%s, qr-token=%s, liff-id=%s', storeId, shiftId, qrToken ? '(exists)' : '(empty)', liffIdParam);
        }
      }

      // URLハッシュにもliff.stateが含まれる場合がある
      if (!storeId || !shiftId || !qrToken || !liffIdParam) {
        if (window.location.hash) {
          const hashParams = new URLSearchParams(window.location.hash.replace('#', '?'));
          const liffState = hashParams.get('liff.state');
          console.log('[LIFF] initializeFromUrl - hash liff.state:', liffState);
          if (liffState) {
            const liffStateParams = this.parseLiffState(liffState);
            if (!storeId) {
              storeId = liffStateParams.get('store-id') || '';
            }
            if (!shiftId) {
              shiftId = liffStateParams.get('shift') || '';
            }
            if (!qrToken) {
              qrToken = liffStateParams.get('qr-token') || '';
            }
            if (!liffIdParam) {
              liffIdParam = liffStateParams.get('liff-id') || '';
            }
            console.log('[LIFF] initializeFromUrl - from hash: store-id=%s, shift=%s, qr-token=%s, liff-id=%s', storeId, shiftId, qrToken ? '(exists)' : '(empty)', liffIdParam);
          }
        }
      }

      console.log('[LIFF] initializeFromUrl - final: store-id=%s, shift=%s, qr-token=%s', storeId, shiftId, qrToken ? '(exists)' : '(empty)');

      // ストレージに保存（ページ更新・ホーム画面起動時のフォールバック用）
      if (storeId) {
        sessionStorage.setItem('liff-store-id', storeId);
        localStorage.setItem('liff-last-store-id', storeId);
      }
      if (qrToken) sessionStorage.setItem('liff-qr-token', qrToken);
      if (shiftId) sessionStorage.setItem('liff-shift-id', shiftId);

      // QRトークンが存在する場合は保持
      if (qrToken) {
        this.qrToken = qrToken;
      }

      // store-idがない場合、shiftパラメータから店舗情報を逆引き
      if (!storeId && shiftId) {
        console.log('[LIFF] initializeFromUrl - store-id not found, trying fallback via shift=%s', shiftId);
        const shiftResult = await apiService.getStoreInfoByShiftId(shiftId);
        if (shiftResult.success && shiftResult.data) {
          storeId = shiftResult.data.store_id;
          this.storeId = storeId;
          this.liffId = shiftResult.data.liff_id;
          sessionStorage.setItem('liff-store-id', storeId);
          localStorage.setItem('liff-last-store-id', storeId);
          // URLバーにstore-idを反映（ホーム画面追加時のショートカットURLにも乗る）
          this.persistStoreIdInUrl();
          console.log('[LIFF] initializeFromUrl - fallback success: store-id=%s, liff-id=%s', storeId, this.liffId);
          return { success: true };
        } else {
          console.error('[LIFF] initializeFromUrl - fallback failed:', shiftResult.error);
        }
      }

      // store-idがない場合、LIFF IDから店舗情報を逆引き
      // liff-idパラメータ or URLパス（liff.line.me/{liffId}/...）から取得
      // ※環境変数等のグローバルなfallbackは使わない（マルチ店舗運用で誤店舗に
      //   ルーティングされる事故を防ぐため。LIFF IDは必ずURL由来で解決する）
      if (!storeId) {
        let resolvedLiffId = liffIdParam;
        if (!resolvedLiffId) {
          // URLがhttps://liff.line.me/{liffId}/...形式の場合、パスからLIFF IDを抽出
          const liffUrlMatch = window.location.href.match(/liff\.line\.me\/([^/?#]+)/);
          if (liffUrlMatch) {
            resolvedLiffId = liffUrlMatch[1];
            console.log('[LIFF] initializeFromUrl - extracted liff-id from URL path: %s', resolvedLiffId);
          }
        }
        if (resolvedLiffId) {
          console.log('[LIFF] initializeFromUrl - store-id not found, trying fallback via liff-id=%s', resolvedLiffId);
          const liffResult = await apiService.getStoreIdByLiffId(resolvedLiffId);
          if (liffResult.success && liffResult.data) {
            storeId = liffResult.data.store_id;
            this.storeId = storeId;
            this.liffId = liffResult.data.liff_id;
            sessionStorage.setItem('liff-store-id', storeId);
            localStorage.setItem('liff-last-store-id', storeId);
            // URLバーにstore-idを反映（ホーム画面追加時のショートカットURLにも乗る）
            this.persistStoreIdInUrl();
            console.log('[LIFF] initializeFromUrl - liff-id fallback success: store-id=%s, liff-id=%s', storeId, this.liffId);
            return { success: true };
          } else {
            console.error('[LIFF] initializeFromUrl - liff-id fallback failed:', liffResult.error);
          }
        }
      }

      // 現在のリンク由来の店舗シグナルが一切得られなかった場合のみ、ストレージの
      // 直近店舗にフォールバックする（ホーム画面ショートカット起動など、URLに店舗情報が
      // 一切乗らないケースの救済。課題83で、現在リンクのシグナルより後段へ移動）。
      if (!storeId) {
        storeId = sessionStorage.getItem('liff-store-id') || localStorage.getItem('liff-last-store-id') || '';
        if (storeId) {
          console.log('[LIFF] initializeFromUrl - fallback to stored store-id: %s', storeId);
        }
      }

      if (!storeId) {
        console.error('[LIFF] initializeFromUrl - store-id not found in URL');
        return { success: false, error: 'URLにstore-idパラメータが見つかりません' };
      }

      const result = await this.setStoreId(storeId);
      console.log('[LIFF] initializeFromUrl - setStoreId result:', result);

      // URLバーに store-id を残す（LIFFブラウザの「リンクをコピー」等で
      // 共有時に store-id が落ちないようにする）
      if (result.success) {
        this.persistStoreIdInUrl();
      }
      return result;
    } catch (error) {
      console.error('[LIFF] initializeFromUrl - error:', error);
      return { success: false, error: '店舗情報の初期化に失敗しました' };
    }
  }

  /**
   * 現在のURLに store-id クエリを残す。
   * LIFFブラウザの「リンクをコピー / シェア」は今のURLを元にパーマネントリンクを
   * 生成するため、URLバーから store-id が消えていると共有先でエラーになる。
   * Reactアプリ起動後も URL に store-id を保持し続けるためのユーティリティ。
   */
  persistStoreIdInUrl(): void {
    if (!this.storeId) return;
    try {
      const url = new URL(window.location.href);
      if (url.searchParams.get('store-id') !== this.storeId) {
        url.searchParams.set('store-id', this.storeId);
        window.history.replaceState(window.history.state, '', url.toString());
      }
    } catch (e) {
      console.warn('[LIFF] persistStoreIdInUrl failed:', e);
    }
  }

  /**
   * 店舗の登録解除後の後始末。
   * 「直近に経由した店舗」として保存中の store-id が解除した店舗のままだと、
   * 次回起動時の check-user-exist が「この店舗に未登録」と判定して
   * 店舗登録ダイアログを再表示してしまうため、キャッシュとURLの store-id を
   * 別の登録中店舗へ差し替える。
   * 他に登録中の店舗が無い場合は差し替え先が無いため何もしない
   * （店舗未登録のままではアプリを利用できないので、次回起動時に
   * 登録ダイアログが出るのが正しい挙動になる）。
   */
  handleStoreUnregistered(unregisteredStoreId: string, fallbackStoreId?: string): void {
    if (!unregisteredStoreId) return;

    // 本人が意図的に解除した店舗を記録（課題60）。次回この店舗のリンク/QRから起動して
    // 「この店舗に未登録」と判定された際、新規店舗と誤認して登録ダイアログを出すのではなく、
    // 他の登録中店舗へ透過的に切り替えるための「一度きり」の目印。
    // fallback の有無に関わらず記録する（登録店舗が1つだけのケースも抑止対象にするため）。
    this.markStoreUnregistered(unregisteredStoreId);

    if (!fallbackStoreId) return;

    // Web Storage は LINEアプリ内ブラウザや iOS プライベートモード /
    // 「サイト越えトラッキングを防ぐ」設定下で getItem/setItem が SecurityError や
    // QuotaExceededError を投げることがある。これは解除成功後の best-effort な後始末で、
    // 失敗しても解除自体は成功のままにすべきなので例外を飲み込む。
    // （ここで throw すると呼び出し元 handleDeleteStore の catch に落ち、
    //   BEでは削除コミット済みなのにUIが「削除に失敗しました」を表示してしまう）
    // sessionStorage と localStorage は独立に失敗しうるため個別に握りつぶし、
    // 片方が失敗しても健全な側の差し替えは行う。
    try {
      if (sessionStorage.getItem('liff-store-id') === unregisteredStoreId) {
        sessionStorage.setItem('liff-store-id', fallbackStoreId);
      }
    } catch (e) {
      console.warn('[LIFF] handleStoreUnregistered sessionStorage update failed:', e);
    }
    try {
      if (localStorage.getItem('liff-last-store-id') === unregisteredStoreId) {
        localStorage.setItem('liff-last-store-id', fallbackStoreId);
      }
    } catch (e) {
      console.warn('[LIFF] handleStoreUnregistered localStorage update failed:', e);
    }

    if (this.storeId === unregisteredStoreId) {
      // PreserveStoreIdParam が getStoreId() の値を URL に付け直すため、
      // メモリ上の storeId も差し替えないと解除済み店舗が URL に残り続ける
      this.storeId = fallbackStoreId;
      this.persistStoreIdInUrl();
    }
  }

  // === 課題60: 意図的に解除した店舗の「一度きり」抑止マーカー ===
  // localStorage に store-id の配列で保持。LINEアプリ内ブラウザ等で Web Storage が
  // 例外を投げることがあるため、読み書きはすべて best-effort（失敗しても機能を止めない）。

  private readUnregisteredStoreIds(): string[] {
    try {
      const raw = localStorage.getItem('liff-unregistered-store-ids');
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed.filter((v) => typeof v === 'string') : [];
    } catch (e) {
      return [];
    }
  }

  private writeUnregisteredStoreIds(ids: string[]): void {
    try {
      localStorage.setItem('liff-unregistered-store-ids', JSON.stringify(ids));
    } catch (e) {
      console.warn('[LIFF] writeUnregisteredStoreIds failed:', e);
    }
  }

  private markStoreUnregistered(storeId: string): void {
    if (!storeId) return;
    const ids = this.readUnregisteredStoreIds();
    if (!ids.includes(storeId)) {
      ids.push(storeId);
      this.writeUnregisteredStoreIds(ids);
    }
  }

  /**
   * 目印があれば true を返し、同時に消費（除去）する。
   * 「解除直後の一度きり」の抑止にとどめ、後日この店舗のリンクを踏んで
   * 再登録したい場合には登録ダイアログが出るようにするため、消費方式にしている。
   */
  private consumeUnregisteredStoreMarker(storeId: string): boolean {
    if (!storeId) return false;
    const ids = this.readUnregisteredStoreIds();
    if (!ids.includes(storeId)) return false;
    this.writeUnregisteredStoreIds(ids.filter((id) => id !== storeId));
    return true;
  }

  private clearUnregisteredStoreMarker(storeId: string): void {
    if (!storeId) return;
    const ids = this.readUnregisteredStoreIds();
    if (ids.includes(storeId)) {
      this.writeUnregisteredStoreIds(ids.filter((id) => id !== storeId));
    }
  }

  /**
   * 現在の店舗が未登録だった場合に、他に登録中の有効店舗へ透過的に切り替える（課題60）。
   * 切り替えに成功したら true。切り替え先が無ければ false を返し、
   * 呼び出し側で従来どおり登録ダイアログにフォールバックする。
   */
  private async switchToAnotherActiveStore(userId: string): Promise<boolean> {
    try {
      const res = await apiService.getStoreUser(userId);
      if (!res?.success || !Array.isArray(res.data) || res.data.length === 0) {
        return false;
      }
      const target = res.data[0];
      if (!target?.store_id) return false;

      const result = await this.setStoreId(target.store_id);
      if (!result.success) return false;

      // キャッシュ / URL も切り替え先店舗へ揃える（best-effort）
      try { sessionStorage.setItem('liff-store-id', target.store_id); } catch (e) { /* best-effort */ }
      try { localStorage.setItem('liff-last-store-id', target.store_id); } catch (e) { /* best-effort */ }
      this.persistStoreIdInUrl();

      // 切り替えた店舗で改めて認証チェック（has_applications 等を正しく反映）。
      // 切り替え先は登録済みのため未登録ブランチへは再入しない（再帰は1段まで）。
      await this.checkUserAuthentication();
      return true;
    } catch (e) {
      console.warn('[LIFF] switchToAnotherActiveStore failed:', e);
      return false;
    }
  }

  /**
   * 店舗IDを設定してLIFF IDを動的に取得
   * @param storeId 店舗ID
   */
  private async setStoreId(storeId: string): Promise<{ success: boolean; error?: string }> {
    try {
      const response = await apiService.getLiffIdByStoreId(storeId);

      
      if (!response.success || !response.data) {
        return { success: false, error: response.error || '取得中にエラーが発生しました' };
      }

      this.storeId = storeId;
      this.liffId = response.data;
      
      return { success: true };
    } catch (error) {
      return { success: false, error: '取得中にエラーが発生しました' };
    }
  }

  // ユーザー認証チェックを別メソッドに分離
  private async checkUserAuthentication(): Promise<void> {
    console.log('[LIFF] checkUserAuthentication - start');
    const isUsingMockLiff = isLiffMockEnabled();

    try {
      // Mock LIFF使用時の処理
      if (isUsingMockLiff) {
        // URLパラメータから取得（URLパラメータが環境変数よりも優先される）
        const urlParams = new URLSearchParams(window.location.search);
        const mockExistingUserParam = urlParams.get('mock-existing-user');
        const mockUserIdParam = urlParams.get('mock-user-id');
        
        // URLパラメータがある場合はそれを優先、なければ環境変数をチェック
        const mockExistingUser = mockExistingUserParam === 'true' || process.env.REACT_APP_MOCK_EXISTING_USER === 'true';
        const mockUserId = mockUserIdParam || process.env.REACT_APP_MOCK_USER_ID || 'mock-user-id-12345';
        
        if (mockExistingUser) {
          // 既存ユーザーとして認証
          if (this.authCallbacks.onUserAuthenticated) {
            this.authCallbacks.onUserAuthenticated(mockUserId);
          }
        } else {
          // 新規ユーザーとして登録画面を表示
          this.handleRegistrationRequired();
        }
        return;
      }
      
      const accessToken = liff.getAccessToken();
      console.log('[LIFF] checkUserAuthentication - accessToken=%s', accessToken ? '(exists)' : '(null)');
      if (!accessToken) throw new Error('アクセストークンが利用できません');

      const response = await apiService.checkUserExist(accessToken, this.storeId);
      console.log('[LIFF] checkUserAuthentication - checkUserExist response:', JSON.stringify(response));
      if (!response.success) {
        // 接続系の一過性エラーはリトライ済みでも解消しなかったケース。
        // バックエンドの生の英語エラー文言をそのままユーザーに見せないようにする。
        if (apiService.isRetryableError(response.error)) {
          throw new Error('通信が不安定なため認証に失敗しました。ページを更新してもう一度お試しください。');
        }
        throw new Error(response.error || 'ユーザー存在チェックに失敗しました');
      }

      // store_id指定時は拡張レスポンス(オブジェクト)、未指定時は従来の文字列
      const data = response.data;

      if (data && typeof data === 'object' && 'user_id' in data) {
        // 拡張レスポンス: store_id指定時
        const userId = data.user_id;
        console.log('[LIFF] checkUserAuthentication - extended response: userId=%s, is_store_registered=%s, has_applications=%s, is_withdrawn=%s', userId, data.is_store_registered, data.has_applications, data.is_withdrawn);

        // 退会済みは店舗登録・解除マーカーのどの分岐より先に判定する（080）。
        // 退会は全店舗の所属を無効化するため、後段の判定では「この店舗だけ未登録」と
        // 見分けが付かず『店舗登録の確認』が出てしまう。マーカーは消費しない
        // （再開後、解除済み店舗の判定に使う必要がある）。
        if (data.is_withdrawn && userId) {
          console.log('[LIFF] checkUserAuthentication - withdrawn account, storeName=%s', data.store_name);
          this.pendingUserId = userId;
          this.pendingLineUserId = data.line_user_id || null;
          if (this.authCallbacks.onReactivationRequired) {
            this.authCallbacks.onReactivationRequired(
              data.store_name || 'この店舗',
              userId
            );
          }
          return;
        }

        if (!data.is_store_registered) {
          // 本人が意図的に解除した店舗への再入場（課題60）では、新規店舗の登録ダイアログを
          // 出さず、他に登録中の店舗があればそちらへ透過的に切り替える。マーカーは一度きり
          // 消費するため、後日この店舗のリンクを踏んで再登録したい場合はダイアログが出る。
          if (userId && this.consumeUnregisteredStoreMarker(this.storeId)) {
            const switched = await this.switchToAnotherActiveStore(userId);
            if (switched) {
              return;
            }
            // 切り替え先が無い（解除したのが唯一の登録店舗など）場合のみ、
            // 従来どおり登録ダイアログにフォールバックする。
          }

          // ユーザーは存在するが、この店舗には未登録
          console.log('[LIFF] checkUserAuthentication - store not registered, storeName=%s', data.store_name);
          this.pendingUserId = userId;
          this.pendingLineUserId = data.line_user_id || null;
          if (this.authCallbacks.onStoreRegistrationRequired) {
            this.authCallbacks.onStoreRegistrationRequired(
              data.store_name || 'この店舗',
              userId
            );
          }
          return;
        }

        // 店舗登録済み
        this.hasApplications = !!data.has_applications;
        console.log('[LIFF] checkUserAuthentication - authenticated: userId=%s, hasApplications=%s', userId, this.hasApplications);
        if (userId && this.authCallbacks.onUserAuthenticated) {
          this.authCallbacks.onUserAuthenticated(userId);
        }
      } else if (data && data !== null) {
        // 従来の文字列レスポンス
        const userId = data;
        
        if (userId && this.authCallbacks.onUserAuthenticated) {
          this.authCallbacks.onUserAuthenticated(userId);
        }
      } else {
        // ユーザーが存在しない場合
        this.handleRegistrationRequired();
      }
    } catch (error) {
      throw error;
    }
  }

  // LINEログインが必要な場合の処理
  private handleLoginRequired(): void {
    
    // AuthContextにログインが必要であることを通知
    // この場合はneedsRegistrationをfalseにして、ログイン待ちであることを示す
    if (this.authCallbacks.onRegistrationRequired) {
      this.authCallbacks.onRegistrationRequired({ 
        needsRegistration: false, 
        isExistingUser: false 
      });
    }
    
    // ログイン状態を更新（ログインが必要な状態として管理）
    this.registrationState = { needsRegistration: false, isExistingUser: false };
  }

  // 新規ユーザー登録が必要な場合の処理
  private handleRegistrationRequired(): void {
    this.registrationState = { needsRegistration: true };
    
    // AuthContextに通知
    if (this.authCallbacks.onRegistrationRequired) {
      this.authCallbacks.onRegistrationRequired(this.registrationState);
    }
  }

  // ユーザー登録処理
  async registerUser(registrationData: UserRegistrationData): Promise<{ success: boolean; error?: string }> {
    if (!this.storeId) {
      return { success: false, error: '店舗情報が不足しています' };
    }

    try {
      const profile = await this.getProfile();
      if (!profile) {
        return { success: false, error: 'LINEプロフィール情報の取得に失敗しました' };
      }

      const createUserData = {
        email: registrationData.email,
        last_name: registrationData.last_name,
        first_name: registrationData.first_name,
        birth_date: registrationData.birth_date,
        phone: registrationData.phone,
        address: registrationData.address,
        postal_code: registrationData.postal_code,
        prefecture: registrationData.prefecture,
        city: registrationData.city,
        street: registrationData.street,
        building: registrationData.building,
        // 利用規約（即給条項含む）とプライバシーポリシーへの同意
        // UserRegistrationPopup でチェックされた値。Backend で必須チェックされる。
        terms_agreed: registrationData.terms_agreed,
        terms_agreed_version: registrationData.terms_agreed_version,
        store_connections: [{
          store_id: this.storeId,
          line_user_id: profile.userId
        }]
      };

      const response = await apiService.createUser(createUserData);
      
      if (response.success) {
        const createdUserData = response.data;
        const userId = createdUserData?.id || createdUserData?.user_id;
        
        // AuthContextに新規ユーザー情報を通知
        if (userId && this.authCallbacks.onUserAuthenticated) {
          this.authCallbacks.onUserAuthenticated(userId);
        }
        
        this.registrationState = { needsRegistration: false };
        return { success: true };
      } else {
        return { success: false, error: response.error || 'ユーザー登録に失敗しました' };
      }
    } catch (error) {
      return { success: false, error: 'ユーザー登録中にエラーが発生しました' };
    }
  }

  // 店舗追加登録処理
  async addStoreRegistration(): Promise<{ success: boolean; error?: string }> {
    if (!this.storeId || !this.pendingUserId) {
      return { success: false, error: '店舗情報またはユーザー情報が不足しています' };
    }

    try {
      let lineUserId = this.pendingLineUserId;
      if (!lineUserId) {
        const profile = await this.getProfile();
        if (!profile) {
          return { success: false, error: 'LINEプロフィール情報の取得に失敗しました' };
        }
        lineUserId = profile.userId;
      }

      const response = await apiService.addStoreRegistration(
        this.pendingUserId,
        this.storeId,
        lineUserId
      );

      if (response.success) {
        const userId = this.pendingUserId;
        // 明示的に再登録した店舗は「解除済み」目印を消す（課題60、消費漏れの保険）
        this.clearUnregisteredStoreMarker(this.storeId);
        this.pendingUserId = null;
        this.pendingLineUserId = null;

        if (userId && this.authCallbacks.onUserAuthenticated) {
          this.authCallbacks.onUserAuthenticated(userId);
        }
        return { success: true };
      } else {
        return { success: false, error: response.error || '店舗登録に失敗しました' };
      }
    } catch (error) {
      return { success: false, error: '店舗登録中にエラーが発生しました' };
    }
  }

  // 退会したアカウントの利用再開処理（080）
  // users.is_active を戻し、退会で無効化された所属店舗もまとめて復活させる。
  // 店舗行だけを戻す addStoreRegistration を使うと退会フラグが残る（ゾンビ状態）ため、
  // 退会済みの再入場では必ずこちらを通す。
  async reactivateAccount(): Promise<{ success: boolean; error?: string }> {
    if (!this.storeId || !this.pendingUserId) {
      return { success: false, error: '店舗情報またはユーザー情報が不足しています' };
    }

    try {
      let lineUserId = this.pendingLineUserId;
      if (!lineUserId) {
        const profile = await this.getProfile();
        lineUserId = profile ? profile.userId : null;
      }

      const response = await apiService.reactivateAccount(
        this.pendingUserId,
        this.storeId,
        lineUserId
      );

      if (response.success) {
        const userId = this.pendingUserId;
        // 再開した店舗は「解除済み」目印を消す（課題60。再開直後に解除扱いされないように）
        this.clearUnregisteredStoreMarker(this.storeId);
        this.pendingUserId = null;
        this.pendingLineUserId = null;

        if (userId && this.authCallbacks.onUserAuthenticated) {
          this.authCallbacks.onUserAuthenticated(userId);
        }
        return { success: true };
      }
      return { success: false, error: response.error || '利用の再開に失敗しました' };
    } catch (error) {
      return { success: false, error: '利用の再開中にエラーが発生しました' };
    }
  }

  // 現在の登録状態を取得
  getRegistrationState(): UserRegistrationState | null {
    return this.registrationState;
  }

  // 登録が必要かどうかを確認
  needsUserRegistration(): boolean {
    return this.registrationState?.needsRegistration === true;
  }

  async login(): Promise<void> {
    if (!this.initialized) {
      await this.initialize();
    }

    if (!liff.isLoggedIn()) {
      liff.login();
    }
  }

  logout(): void {
    if (liff.isLoggedIn()) {
      liff.logout();
    }
  }

  isLoggedIn(): boolean {
    return liff.isLoggedIn();
  }

  isInClient(): boolean {
    return liff.isInClient();
  }

  async getProfile(): Promise<LiffProfile | null> {
    if (!this.isLoggedIn()) {
      return null;
    }

    try {
      return await liff.getProfile();
    } catch (error) {
      return null;
    }
  }

  async getAccessToken(): Promise<string | null> {
    if (!this.isLoggedIn()) {
      return null;
    }

    try {
      return liff.getAccessToken();
    } catch (error) {
      return null;
    }
  }

  async getEmail(): Promise<string | null> {
    if (!this.isLoggedIn()) {
      return null;
    }

    try {
      const liffIdToken = liff.getDecodedIDToken();
      return liffIdToken?.email || null;
    } catch (error) {
      return null;
    }
  }

  openWindow(url: string, external: boolean = false): void {
    liff.openWindow({ url, external });
  }

  getStoreId(): string {
    return this.storeId;
  }

  getQrToken(): string {
    return this.qrToken;
  }

  /**
   * 出退勤QRのトークンを「1回だけ」取り出す（ワンショット消費）。
   *
   * qr-token は liff.state 経由でURLパラメータが落ちるケースの救済として
   * sessionStorage にも保持している。ただし残り続けると、一度でも店舗QRから起動した
   * セッションでは以降どの画面遷移でも「QRで起動した」と判定され、出勤予定表が常に
   * 出勤・退勤画面に化ける（別店舗のQRで起動した後はその別店舗の画面が出続ける）。
   * そのため打刻画面が受け取った時点で破棄する。
   */
  consumeQrToken(): { qrToken: string; storeId: string } {
    const qrToken = this.qrToken;
    const storeId = this.storeId;
    this.qrToken = '';
    try {
      sessionStorage.removeItem('liff-qr-token');
    } catch {
      // LINE内ブラウザ等でストレージが使えない場合は握りつぶす（メモリ側は破棄済み）
    }
    return { qrToken, storeId };
  }

  getHasApplications(): boolean {
    return this.hasApplications;
  }

  closeWindow(): void {
    this.initialized = false;
    liff.closeWindow();
  }
}

const liffService = new LiffService();
export default liffService;